// Brand tokens — QEA FieldLens (Insight Delivered).
//
// Values carried verbatim from:
//   C:\QEA-Shared\DESIGN.md §1.1 / §5.1
//   C:\QEA-Shared\Insight Delivered Brand Guidelines.pdf v1.0
//
// When/if `@insightdelivered/theme` lands at `C:\QEA-Shared\packages\`
// (DESIGN.md §11 — 3-app promotion threshold), swap the `tokens` object
// to `import { tokens } from '@insightdelivered/theme'` and delete this
// local copy.
//
// Layout parity with FinanceLens: sidebar is a "dark island" that stays
// indigo across light AND dark modes; only the top bar + content adapt.
// See Sidebar.tsx — SB_* constants below feed it directly.

import { createTheme, type ThemeOptions } from '@mui/material/styles'

export const tokens = {
  // Primary
  indigo:      '#29265B',
  cloudGrey:   '#C8D1D7',

  // Secondary
  gold:        '#F6AF33',   // CTAs only
  alert:       '#E5501C',   // brand error — NOT #E84545
  charcoal:    '#262E31',   // text.primary — NEVER #000000
  lightBg:     '#F5F5F5',

  // Tints / state
  indigoLight: '#3D3880',
  indigoDark:  '#1B1940',
  goldLight:   '#FDE9BB',
  bgDefault:   '#F0F2F5',
  bgPaper:     '#FFFFFF',
  textSec:     '#6B7280',
  divider:     '#E0E4EC',

  // Semantic
  success:     '#22C55E',
  warning:     '#F59E0B',   // prefer `alert` for hard warnings
  error:       '#E5501C',
  info:        '#3B82F6',
} as const

// Sidebar tokens are mode-invariant — the sidebar stays indigo in dark
// mode too (parity with FinanceLens/BDXLens). Consumed by Sidebar.tsx.
export const SB = {
  BG_MID:     '#1A1740',
  BG_DEEP:    tokens.indigoDark,
  BORDER:     'rgba(200, 209, 215, 0.14)',
  TEXT:       '#F0F2F5',
  TEXT_DIM:   'rgba(200, 209, 215, 0.62)',
  ICON_DIM:   'rgba(200, 209, 215, 0.55)',
  ACCENT:     tokens.gold,
} as const

export type ThemeMode = 'light' | 'dark'

// Dark-mode palette. Keep contrasts legible; do NOT invert the sidebar
// (SB_* stays indigo). Anchors: cards read as slightly-lifted surfaces
// on a near-black-blue ground so charts don't lose their axis grid.
const darkTokens = {
  bgDefault: '#0E0C24',   // page ground — deeper than sidebar so nav reads as elevated
  bgPaper:   '#181638',   // card surface
  textPri:   '#F0F2F5',
  textSec:   'rgba(240,242,245,0.70)',
  divider:   'rgba(240,242,245,0.10)',
} as const

function makeThemeOptions(mode: ThemeMode): ThemeOptions {
  const isDark = mode === 'dark'
  return {
    palette: {
      mode,
      primary:   { main: tokens.indigo, light: tokens.indigoLight, dark: tokens.indigoDark, contrastText: '#FFFFFF' },
      secondary: { main: tokens.gold,   light: tokens.goldLight, contrastText: tokens.charcoal },
      error:     { main: tokens.alert },
      warning:   { main: tokens.warning },
      info:      { main: tokens.info },
      success:   { main: tokens.success },
      text:      isDark
        ? { primary: darkTokens.textPri,  secondary: darkTokens.textSec }
        : { primary: tokens.charcoal,     secondary: tokens.textSec },
      background: isDark
        ? { default: darkTokens.bgDefault, paper: darkTokens.bgPaper }
        : { default: tokens.bgDefault,     paper: tokens.bgPaper },
      divider: isDark ? darkTokens.divider : tokens.divider,
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      h1:       { fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em' },
      h2:       { fontSize: '1.4rem',  fontWeight: 700 },
      h3:       { fontSize: '1.15rem', fontWeight: 700 },
      h4:       { fontSize: '1rem',    fontWeight: 700 },
      h5:       { fontSize: '0.9rem',  fontWeight: 600 },
      h6:       { fontSize: '0.8rem',  fontWeight: 600 },
      body1:    { fontSize: '0.875rem' },
      body2:    { fontSize: '0.8rem' },
      caption:  { fontSize: '0.72rem', color: isDark ? darkTokens.textSec : tokens.textSec },
      overline: { fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em' },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 8 },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 4, height: 6 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            // Kill MUI's dark-mode "elevation overlay" that lifts every
            // Paper toward white. Our dark cards are already tinted; we
            // don't want a second white wash on top.
            backgroundImage: 'none',
          },
        },
      },
    },
  }
}

export function makeTheme(mode: ThemeMode) {
  return createTheme(makeThemeOptions(mode))
}

// Back-compat export — the pre-shell app imported `theme` directly.
// Keep it pointing at the light theme so any straggling imports keep
// working while the codebase migrates to `useThemeMode()`.
export const theme = makeTheme('light')
